import React, { useState } from 'react';
import { Plus, Edit2, Trash2, X, Check } from 'lucide-react';
import { create as createRole, update as updateRole, remove as removeRole } from '../../api/roles';

const C = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', text: '#e2e8f0', muted: '#64748b',
  subtle: '#94a3b8', green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
};

export default function RolesView({ roles, onAdd, onUpdate, onRemove }) {
  const [search,        setSearch]        = useState('');
  const [showAddRow,    setShowAddRow]     = useState(false);
  const [addName,       setAddName]        = useState('');
  const [addErr,        setAddErr]         = useState('');
  const [addSaving,     setAddSaving]      = useState(false);
  const [editId,        setEditId]         = useState(null);
  const [editName,      setEditName]       = useState('');
  const [editErr,       setEditErr]        = useState('');
  const [editSaving,    setEditSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete]  = useState(null);
  const [deleting,      setDeleting]       = useState(false);
  const [delErr,        setDelErr]         = useState('');

  const filtered = roles.filter(r =>
    !search || r.name.toLowerCase().includes(search.toLowerCase())
  );

  async function handleAdd() {
    if (!addName.trim()) return setAddErr('Role name is required');
    setAddSaving(true); setAddErr('');
    try {
      const role = await createRole({ name: addName.trim() });
      onAdd(role);
      setAddName(''); setShowAddRow(false);
    } catch (e) {
      setAddErr(e.response?.data?.error || 'Failed to save');
    }
    setAddSaving(false);
  }

  function startEdit(role) { setEditId(role.id); setEditName(role.name); setEditErr(''); }
  function cancelEdit()    { setEditId(null); setEditName(''); setEditErr(''); }

  async function handleEdit(role) {
    if (!editName.trim()) return setEditErr('Role name is required');
    setEditSaving(true); setEditErr('');
    try {
      const updated = await updateRole(role.id, { name: editName.trim() });
      onUpdate(updated);
      cancelEdit();
    } catch (e) {
      setEditErr(e.response?.data?.error || 'Failed to save');
    }
    setEditSaving(false);
  }

  async function handleDelete(role) {
    setDeleting(true); setDelErr('');
    try {
      await removeRole(role.id);
      onRemove(role.id);
      setConfirmDelete(null);
    } catch (e) {
      setDelErr(e.response?.data?.error || 'Delete failed');
    }
    setDeleting(false);
  }

  const inputSt = { padding: '7px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: '13px', fontFamily: 'inherit', outline: 'none' };
  const btnBase = { padding: '5px 10px', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', display: 'flex', alignItems: 'center', gap: '4px' };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '28px', background: C.bg }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.text, marginBottom: '4px' }}>Roles</h2>
          <p style={{ fontSize: '13px', color: C.muted }}>Global roles available for employee assignment across all sites.</p>
        </div>
        <button onClick={() => { setShowAddRow(true); setAddErr(''); setAddName(''); }}
          style={{ ...btnBase, background: C.blue, color: '#fff', padding: '9px 18px' }}>
          <Plus size={14} /> Add Role
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder='Search roles…'
          style={{ ...inputSt, minWidth: '220px' }} />
      </div>

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        {/* Header row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, background: C.elevated }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role Name</span>
          <span></span>
        </div>

        {/* Add row */}
        {showAddRow && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, alignItems: 'center', gap: '12px' }}>
            <div>
              <input autoFocus value={addName} onChange={e => setAddName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setShowAddRow(false); }}
                placeholder='e.g. Supervisor'
                style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }} />
              {addErr && <div style={{ fontSize: '11px', color: C.red, marginTop: '4px' }}>{addErr}</div>}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={handleAdd} disabled={addSaving}
                style={{ ...btnBase, background: C.blue, color: '#fff' }}>
                <Check size={12} />{addSaving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setShowAddRow(false)}
                style={{ ...btnBase, background: C.elevated, color: C.muted }}>
                <X size={12} />
              </button>
            </div>
          </div>
        )}

        {filtered.length === 0 && !showAddRow && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>
            {roles.length === 0 ? 'No roles yet — click Add Role to create the first one.' : 'No roles match your search.'}
          </div>
        )}

        {filtered.map(role => (
          <div key={role.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '12px 16px', borderBottom: `1px solid ${C.border}`, alignItems: 'center', gap: '12px' }}>
            {editId === role.id ? (
              <div>
                <input autoFocus value={editName} onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleEdit(role); if (e.key === 'Escape') cancelEdit(); }}
                  style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }} />
                {editErr && <div style={{ fontSize: '11px', color: C.red, marginTop: '4px' }}>{editErr}</div>}
              </div>
            ) : (
              <span style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{role.name}</span>
            )}

            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
              {editId === role.id ? (
                <>
                  <button onClick={() => handleEdit(role)} disabled={editSaving}
                    style={{ ...btnBase, background: C.blue, color: '#fff' }}>
                    <Check size={12} />{editSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={cancelEdit}
                    style={{ ...btnBase, background: C.elevated, color: C.muted }}>
                    <X size={12} />
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => startEdit(role)}
                    style={{ ...btnBase, background: C.elevated, color: C.subtle }}>
                    <Edit2 size={12} />
                  </button>
                  <button onClick={() => { setConfirmDelete(role); setDelErr(''); }}
                    style={{ ...btnBase, background: C.red + '22', color: C.red, border: `1px solid ${C.red}44` }}>
                    <Trash2 size={12} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Delete confirmation */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px', width: '360px', maxWidth: '95vw' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: C.text, marginBottom: '8px' }}>Delete Role</div>
            <div style={{ fontSize: '13px', color: C.subtle, marginBottom: '16px' }}>
              Delete <strong style={{ color: C.text }}>{confirmDelete.name}</strong>? Employees assigned this role will retain their current role value but it will no longer appear in the dropdown.
            </div>
            {delErr && <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '8px 12px', marginBottom: '12px', fontSize: '12px', color: C.red }}>{delErr}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={deleting}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: C.red, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: deleting ? 0.7 : 1 }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
